import { FunctionComponent, memo, useCallback, useContext, useMemo } from "react";
import { IPhraseTriggerTableData, IProps } from "./phrase-triggers-table.types";
import usePhraseTriggers from "hooks-for/phrase-triggers/use-phrase-triggers";
import { Button, SpinnerUntil } from "@edgetier/components";
import { SearchType } from "@edgetier/watchtower-types";
import { Box, BoxContent, BoxHeader, BoxHeading, LoadingCell, None, NumericValue } from "@edgetier/client-components";
import { Cell, Column, Heading, Table, TableColumns } from "@edgetier/client-components";
import { useLanguages } from "hooks-for/filters/use-languages";
import InteractionTypesSummary from "./interaction-types-summary";
import LanguagesSummary from "./languages-summary";
import MessageSourcesSummary from "./message-sources-summary";
import PhraseTriggersActionsMenu from "../phrase-triggers-actions-menu";
import { faPlus } from "@fortawesome/pro-solid-svg-icons";
import { generatePath, useHistory } from "react-router-dom";
import Page from "constants/page";
import { trackUser } from "utilities/track-user";
import useInteractionsCounts from "hooks-for/interactions/use-interactions-counts";
import { PHRASE_TRIGGER_GROUP } from "../phrase-triggers.constants";
import { PhraseTriggerContext } from "components-for/phrase-triggers/phrase-trigger-context/phrase-trigger-context";
import { getPhraseTriggerCountData } from "./phrase-triggers-table.utilities";
import { useUser } from "hooks-for/users/use-user";
import { EXCLUDED_CLIENT_ID } from "./phrase-triggers-table.constants";
import "./phrase-triggers-table.scss";

/**
 * A table showing all the phrase triggers for a particular tag.
 * @param props.tagId The ID of the tag for the phrase triggers being displayed.
 */
const PhraseTriggersTable: FunctionComponent<IProps> = ({ tagId }) => {
    const history = useHistory();
    const { filters } = useContext(PhraseTriggerContext);

    const { data: phraseTriggers, isLoading: isPhraseTriggersLoading } = usePhraseTriggers({ tagId });
    const { data: languages, isLoading: areLanguagesLoading } = useLanguages({}, { staleTime: 1000 * 60 * 30 });

    const { data: user, isLoading: isUserLoading } = useUser();
    const isExcluded = user?.clientId === EXCLUDED_CLIENT_ID;

    // Get counts of phrases or interactions according to the parameters.
    const { data: filteredData, isLoading: isFilteredDataLoading } = useInteractionsCounts(
        {
            startDateTime: filters.startDateTime,
            endDateTime: filters.endDateTime,
            tagId: [tagId],
            groupBy: [filters.groupBy, PHRASE_TRIGGER_GROUP],
        },
        { enabled: typeof user !== "undefined" && !isExcluded }
    );

    // Generate a link for phrase trigger performance report
    const phraseTriggerReport = useCallback(
        (phraseTriggerId: string) => {
            return generatePath(
                Page.PhraseTriggersReport.replace(":tagId", tagId).replace(":phraseTriggerId", phraseTriggerId)
            );
        },
        [tagId]
    );

    // Redirect to create phrase trigger screen;
    const createPhraseTrigger = () => {
        trackUser("Create Phrase Trigger");
        history.push(Page.ManagePhraseTriggers.replace(":tagId", tagId));
    };

    const phraseTriggerCountData = getPhraseTriggerCountData(filteredData);

    // Sum of total count from all phrase triggers
    const totalInteractionsTagged = useMemo(
        () =>
            Object.values(phraseTriggerCountData).reduce((sum, currentTriggerCount) => (sum += currentTriggerCount), 0),
        [phraseTriggerCountData]
    );

    // Extend phrase trigger to include a 'count' property from 'phraseTriggerCountData'
    const updatedPhraseTriggers = (phraseTriggers ?? []).map((phraseTriggerEntry) => ({
        ...phraseTriggerEntry,
        interactionCount: phraseTriggerCountData[phraseTriggerEntry.phraseTriggerId] ?? 0,
    }));

    return (
        <>
            <Box>
                <BoxHeader>
                    <BoxHeading>
                        <div>
                            <span>Phrase Triggers </span>
                            {Array.isArray(phraseTriggers) && phraseTriggers.length > 0 && (
                                <span>({phraseTriggers.length} triggers)</span>
                            )}
                        </div>
                    </BoxHeading>
                </BoxHeader>

                <BoxContent className="phrase-triggers-table">
                    <SpinnerUntil<IPhraseTriggerTableData[]>
                        data={updatedPhraseTriggers}
                        isReady={!isPhraseTriggersLoading && !isUserLoading && !areLanguagesLoading}
                    >
                        {(data) => (
                            <div className="phrase-triggers-table__content">
                                <Table<IPhraseTriggerTableData>
                                    getRowKey={({ phraseTriggerId }) => phraseTriggerId}
                                    rows={data}
                                    isInBox
                                    className="phrase-triggers-table"
                                    headerIsSticky
                                    getRowLink={({ phraseTriggerId }) => phraseTriggerReport(phraseTriggerId)}
                                >
                                    <TableColumns>
                                        <Column>
                                            <Heading>Phrase</Heading>
                                            <Cell<IPhraseTriggerTableData>>
                                                {({ phrase }) => (
                                                    <div className="phrase-triggers__content-phrase">{phrase}</div>
                                                )}
                                            </Cell>
                                        </Column>
                                        {!isExcluded && (
                                            <Column>
                                                <Heading
                                                    sortProperty={
                                                        !isFilteredDataLoading ? "interactionCount" : undefined
                                                    }
                                                >
                                                    Interactions Tagged
                                                </Heading>
                                                <Cell<IPhraseTriggerTableData>>
                                                    {({ interactionCount }) => (
                                                        <>
                                                            {isFilteredDataLoading && !isExcluded ? (
                                                                <LoadingCell />
                                                            ) : (
                                                                <div className="phrase-triggers__counts">
                                                                    <NumericValue value={interactionCount} />{" "}
                                                                    <span className="phrase-triggers__percentage">
                                                                        (
                                                                        <NumericValue
                                                                            value={
                                                                                interactionCount > 0
                                                                                    ? (interactionCount /
                                                                                          totalInteractionsTagged) *
                                                                                      100
                                                                                    : 0
                                                                            }
                                                                            unit="%"
                                                                            options={{
                                                                                minimumFractionDigits: 1,
                                                                                maximumFractionDigits: 1,
                                                                            }}
                                                                        />
                                                                        )
                                                                    </span>
                                                                </div>
                                                            )}
                                                        </>
                                                    )}
                                                </Cell>
                                            </Column>
                                        )}
                                        <Column>
                                            <Heading>Match Type</Heading>
                                            <Cell<IPhraseTriggerTableData>>
                                                {({ searchTypeId }) => SearchType[searchTypeId]}
                                            </Cell>
                                        </Column>
                                        <Column>
                                            <Heading>Interaction Type</Heading>
                                            <Cell<IPhraseTriggerTableData>>
                                                {({ interactionTypes }) => (
                                                    <InteractionTypesSummary interactionTypes={interactionTypes} />
                                                )}
                                            </Cell>
                                        </Column>
                                        <Column>
                                            <Heading>Message Source</Heading>
                                            <Cell<IPhraseTriggerTableData>>
                                                {(phraseTrigger) => (
                                                    <MessageSourcesSummary phraseTrigger={phraseTrigger} />
                                                )}
                                            </Cell>
                                        </Column>
                                        <Column>
                                            <Heading>Language</Heading>
                                            <Cell<IPhraseTriggerTableData>>
                                                {({ languageIsoCodes }) => (
                                                    <LanguagesSummary
                                                        languageIsoCodes={languageIsoCodes}
                                                        languages={languages ?? []}
                                                    />
                                                )}
                                            </Cell>
                                        </Column>
                                        <Column>
                                            <Heading>Threshold</Heading>
                                            <Cell<IPhraseTriggerTableData>>
                                                {({ searchTypeId, similarityThreshold }) => (
                                                    <>
                                                        {searchTypeId === SearchType.Similarity ? (
                                                            <div>
                                                                <NumericValue
                                                                    value={similarityThreshold * 100}
                                                                    unit="%"
                                                                    options={{
                                                                        minimumFractionDigits: 2,
                                                                        maximumFractionDigits: 2,
                                                                    }}
                                                                />
                                                            </div>
                                                        ) : (
                                                            <None>N/A</None>
                                                        )}
                                                    </>
                                                )}
                                            </Cell>
                                        </Column>
                                        <Column>
                                            <Heading>&nbsp;</Heading>
                                            <Cell<IPhraseTriggerTableData> excludeRowLink>
                                                {({ phraseTriggerId }) => (
                                                    <span onClick={(event) => event.stopPropagation()}>
                                                        <PhraseTriggersActionsMenu phraseTriggerId={phraseTriggerId} />
                                                    </span>
                                                )}
                                            </Cell>
                                        </Column>
                                    </TableColumns>
                                </Table>
                            </div>
                        )}
                    </SpinnerUntil>
                </BoxContent>
            </Box>
            <div className="phrase-triggers-table__create-button">
                <Button className="create-phrase-trigger-button" icon={faPlus} onClick={createPhraseTrigger}>
                    Create Phrase Trigger
                </Button>
            </div>
        </>
    );
};
export default memo(PhraseTriggersTable);
